import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { api, ApiError } from "../../api";
import { Spinner } from "../../components/ui";
import { AssistantPanel } from "./components";

export type Trail = {
  course: {
    id: string;
    title: string;
    description: string | null;
    workloadHours: number | null;
    coverUrl: string | null;
  };
  settings: { sequentialUnlock: boolean; passingScore: number };
  enrollment: {
    status: string;
    progressPercent: number;
    completedLessons: number;
    totalLessons: number;
    resumeLessonId: string | null;
  };
  modules: Array<{
    id: string;
    position: number;
    title: string;
    summary: string | null;
    unlocked: boolean;
    lessons: Array<{
      id: string;
      position: number;
      title: string;
      readingMinutes: number | null;
      state: "locked" | "available" | "in_progress" | "completed";
    }>;
  }>;
  flashcardsDue: { due: number; total: number; learned: number };
  final: {
    available: boolean;
    questions: number;
    bestScore: number | null;
    passed: boolean;
    required: boolean;
  };
  certificate: { code: string; issuedAt: string } | null;
  points: number;
  badges: Array<{ code: string; name: string; icon: string; description: string; awardedAt: string | null }>;
  ranking: null | {
    top: Array<{ position: number; name: string; points: number; me: boolean }>;
    me: { position: number; points: number } | null;
    total: number;
  };
};

const STATE_LABEL = {
  locked: "Bloqueada",
  available: "Disponível",
  in_progress: "Em andamento",
  completed: "Concluída",
};

function StateIcon({ state }: { state: keyof typeof STATE_LABEL }) {
  return (
    <span className={`ls-icon ${state}`} aria-label={STATE_LABEL[state]} title={STATE_LABEL[state]}>
      {state === "completed" ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
        >
          <path d="M5 12l5 5 9-10" />
        </svg>
      ) : state === "locked" ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      ) : null}
    </span>
  );
}

/** Trilha do curso: progresso, retomada, aulas por módulo, revisão, prática, avaliação e certificado. */
export function CourseTrailPage() {
  const { courseId = "" } = useParams();
  const [t, setT] = useState<Trail | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get<Trail>(`/learn/${courseId}`)
      .then(setT)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar o curso."));
  }, [courseId]);

  if (error)
    return (
      <main className="page">
        <p className="alert error">{error}</p>
        <Link to="/aluno">Voltar aos meus cursos</Link>
      </main>
    );
  if (!t)
    return (
      <main className="page">
        <Spinner />
      </main>
    );

  const e = t.enrollment;
  const resume = t.modules.flatMap((m) => m.lessons).find((l) => l.id === e.resumeLessonId);
  return (
    <main className="page">
      <section className="trail-hero card">
        <div className="trail-cover">
          {t.course.coverUrl ? (
            <img src={t.course.coverUrl} alt="" />
          ) : (
            <span>{t.course.title.slice(0, 1)}</span>
          )}
        </div>
        <div className="card-pad trail-info">
          <div className="eyebrow">
            <Link to="/aluno">Meus cursos</Link>
          </div>
          <h1>{t.course.title}</h1>
          {t.course.description && <p className="segment-meta">{t.course.description}</p>}
          <div className="progress big" aria-label={`Progresso ${e.progressPercent}%`}>
            <span style={{ width: `${Math.max(2, e.progressPercent)}%` }} />
          </div>
          <p className="segment-meta">
            {e.progressPercent}% · {e.completedLessons} de {e.totalLessons} aulas
            {t.course.workloadHours ? ` · ${t.course.workloadHours} h` : ""}
          </p>
          <div className="actions" style={{ justifyContent: "flex-start" }}>
            {t.certificate ? (
              <a className="btn" href={`/api/learn/${courseId}/certificate.pdf`}>
                Baixar certificado
              </a>
            ) : resume ? (
              <Link className="btn" to={`/aluno/cursos/${courseId}/aulas/${resume.id}`}>
                {e.completedLessons ? "Continuar de onde parei" : "Começar o curso"}
              </Link>
            ) : t.final.available ? (
              <Link className="btn" to={`/aluno/cursos/${courseId}/avaliacao`}>
                Fazer a avaliação final
              </Link>
            ) : null}
            <Link className="btn ghost" to={`/aluno/cursos/${courseId}/consulta`}>
              Glossário, busca e anotações
            </Link>
          </div>
        </div>
      </section>

      <div className="trail-grid">
        <div>
          {t.modules.map((m) => (
            <section key={m.id} className={`card module-card${m.unlocked ? "" : " is-locked"}`}>
              <div className="module-head">
                <div>
                  <div className="eyebrow">Módulo {m.position}</div>
                  <h2>{m.title}</h2>
                </div>
                <span className="segment-meta">
                  {m.lessons.filter((l) => l.state === "completed").length}/{m.lessons.length}
                </span>
              </div>
              <ol className="lesson-steps">
                {m.lessons.map((l) => (
                  <li key={l.id} className={l.state}>
                    <StateIcon state={l.state} />
                    {l.state === "locked" ? (
                      <span className="lesson-link disabled">
                        {m.position}.{l.position} {l.title}
                      </span>
                    ) : (
                      <Link className="lesson-link" to={`/aluno/cursos/${courseId}/aulas/${l.id}`}>
                        {m.position}.{l.position} {l.title}
                      </Link>
                    )}
                    <span className="segment-meta">{l.readingMinutes ? `${l.readingMinutes} min` : ""}</span>
                  </li>
                ))}
              </ol>
              {m.unlocked && (
                <div className="module-practice">
                  <Link to={`/aluno/cursos/${courseId}/revisao?modulo=${m.id}`}>Flashcards</Link>
                  <Link to={`/aluno/cursos/${courseId}/modulos/${m.id}`}>Estudo de caso e exercícios</Link>
                </div>
              )}
            </section>
          ))}
        </div>
        <aside className="trail-side">
          <section className="card card-pad">
            <div className="eyebrow">Revisão do dia</div>
            <div className="big-num">{t.flashcardsDue.due}</div>
            <p className="segment-meta">
              flashcard(s) para revisar · {t.flashcardsDue.learned} de {t.flashcardsDue.total} memorizados
            </p>
            <Link
              className={`btn small${t.flashcardsDue.due ? "" : " ghost"}`}
              to={`/aluno/cursos/${courseId}/revisao`}
            >
              {t.flashcardsDue.due ? "Revisar agora" : "Ver flashcards"}
            </Link>
          </section>
          <section className={`card card-pad${t.final.available ? "" : " is-locked"}`}>
            <div className="eyebrow">Avaliação final</div>
            {t.final.passed ? (
              <p>
                Aprovado(a) com <strong>{t.final.bestScore}%</strong>.
              </p>
            ) : t.final.available ? (
              <p className="segment-meta">
                {t.final.questions} questões sorteadas
                {t.settings.passingScore ? ` · nota mínima ${t.settings.passingScore}%` : ""}
                {t.final.bestScore !== null ? ` · sua melhor nota: ${t.final.bestScore}%` : ""}
              </p>
            ) : (
              <p className="segment-meta">Liberada após a conclusão de todas as aulas.</p>
            )}
            {t.final.available && !t.final.passed && (
              <Link className="btn small" to={`/aluno/cursos/${courseId}/avaliacao`}>
                {t.final.bestScore !== null ? "Tentar novamente" : "Iniciar avaliação"}
              </Link>
            )}
          </section>
          <section className="card card-pad">
            <div className="eyebrow">Conquistas · {t.points} pontos</div>
            <ul className="badge-grid">
              {t.badges.map((b) => (
                <li
                  key={b.code}
                  className={b.awardedAt ? "got" : ""}
                  title={`${b.name}: ${b.description}`}
                  aria-label={`${b.name}${b.awardedAt ? " (conquistada)" : " (a conquistar)"}: ${b.description}`}
                >
                  <span aria-hidden>{b.icon}</span>
                  <small>{b.name}</small>
                </li>
              ))}
            </ul>
          </section>
          {t.ranking && (
            <section className="card card-pad">
              <div className="eyebrow">Ranking da turma</div>
              <ol className="ranking">
                {t.ranking.top.map((r) => (
                  <li key={r.position} className={r.me ? "me" : ""}>
                    <span className="pos">{r.position}º</span>
                    <span className="nm">{r.me ? "Você" : r.name}</span>
                    <span className="pts">{r.points} pts</span>
                  </li>
                ))}
              </ol>
              {t.ranking.me && !t.ranking.top.some((r) => r.me) && (
                <p className="segment-meta">
                  Sua posição: {t.ranking.me.position}º de {t.ranking.total} ({t.ranking.me.points} pts)
                </p>
              )}
            </section>
          )}
          <section className={`card card-pad cert-card${t.certificate ? " done" : ""}`}>
            <div className="eyebrow">Certificado</div>
            {t.certificate ? (
              <>
                <p>
                  Emitido em {new Date(t.certificate.issuedAt).toLocaleDateString("pt-BR")}.<br />
                  <span className="segment-meta">Código {t.certificate.code}</span>
                </p>
                <a className="btn small" href={`/api/learn/${courseId}/certificate.pdf`}>
                  Baixar PDF
                </a>{" "}
                <Link className="btn ghost small" to={`/certificado/${t.certificate.code}`}>
                  Página de validação
                </Link>
              </>
            ) : (
              <p className="segment-meta">
                Disponível ao concluir as aulas
                {t.final.required ? " e ser aprovado(a) na avaliação final" : ""}.
              </p>
            )}
          </section>
        </aside>
      </div>
      <AssistantPanel courseId={courseId} />
    </main>
  );
}
